import { Box, Button, FormGroup, Grid, IconButton } from "@mui/material"
import { ChangeEvent, ReactElement, useState } from "react"
import { StyledMuiFieldLabel } from "./muiFields/MuiFIeldLabel"
import {
  DeleteOutline,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material"
import {
  Field,
  FieldArray,
  FieldArrayRenderProps,
  useFormikContext,
} from "formik"
import { ISolution } from "../models/Solution"
import { IPrintedObject } from "../models/Printing"
import { Separator } from "./Separator"
import { PlusButton } from "./PlusButton"
import MuiField from "./MuiField"
import { CheckboxField } from "./Checkbox"

export function ObjectForm(props: {
  oi: number
  objectArrayHelpers: FieldArrayRenderProps
  objectVlues: IPrintedObject
}): ReactElement {
  const [isHidden, Hide] = useState(props.objectVlues.formHidden)
  const { values, setFieldValue } = useFormikContext<ISolution>()
  const valuePath = `printing.objects[${props.oi}]`

  return (
    <FormGroup key={props.oi}>
      <Box
        sx={{
          "> *": {
            padding: "5px 0 5px 0",
          },
          border: "1px solid #dfe0f2",
          borderRadius: "14px",
          padding: "10px 25px 10px 25px",
          backgroundColor: "white",
        }}
      >
        <Grid
          container
          height="45px"
          direction="row"
          justifyContent="space-between"
          alignItems="start"
          sx={{ margin: "0px" }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ marginInlineEnd: "20px" }}>
              <IconButton
                sx={{
                  ":hover": { backgroundColor: "white" },
                  "& :hover": { backgroundColor: "#250048", color: "white" },
                }}
                onClick={() => {
                  Hide(!isHidden)
                  setFieldValue(`${valuePath}.formHidden`, !isHidden)
                }}
              >
                {!isHidden && (
                  <KeyboardArrowUp
                    sx={{
                      color: "#250048",
                      backgroundColor: "#eef2f6",
                      borderRadius: "999px",
                    }}
                  />
                )}
                {isHidden && (
                  <KeyboardArrowDown
                    sx={{
                      color: "#250048",
                      backgroundColor: "#eef2f6",
                      borderRadius: "999px",
                    }}
                  />
                )}
              </IconButton>
            </Box>

            <Field
              name={`${valuePath}.title`}
              type="rawText"
              component={MuiField}
              helperText="title"
              value={5}
            />
          </Box>
          <IconButton
            onClick={() => props.objectArrayHelpers.remove(props.oi)}
            sx={{
              ":hover": { backgroundColor: "white" },
              "& :hover": { backgroundColor: "#250048", color: "white" },
            }}
          >
            <DeleteOutline sx={{ color: "#250048", borderRadius: "3px" }} />
          </IconButton>
        </Grid>

        {!isHidden && (
          <>
            <Box sx={{ marginBlockStart: "10px" }}>
              <Separator />
            </Box>
            <Field
              xsmd={[6, 6]}
              name={`${valuePath}.wImg`}
              label="Draw with image (.svg)"
              type="checkbox"
              component={CheckboxField}
            />

            {props.objectVlues.wImg ? (
              <>
                <Field
                  xsmd={[6, 6]}
                  name={`${valuePath}.height`}
                  label="Height (mm)"
                  type="number"
                  component={MuiField}
                  alignRight
                />
                <ObjectFile
                  oi={props.oi}
                  setBase64={(newFile) =>
                    setFieldValue(`${valuePath}.img64`, newFile)
                  }
                  setFileName={(newFileName) =>
                    setFieldValue(`${valuePath}.imgFileName`, newFileName)
                  }
                  fileName={values.printing.objects[props.oi].imgFileName}
                />
              </>
            ) : (
              <>
                <Field
                  xsmd={[6, 6]}
                  name={`${valuePath}.isCircle`}
                  label="Logo is circle shaped"
                  type="checkbox"
                  component={CheckboxField}
                />
                {props.objectVlues.isCircle && (
                  <>
                    <Field
                      xsmd={[6, 6]}
                      name={`${valuePath}.radius`}
                      label="radius"
                      type="number"
                      component={MuiField}
                      alignRight
                    />
                  </>
                )}
                {!props.objectVlues.isCircle && (
                  <>
                    <Field
                      xsmd={[6, 6]}
                      name={`${valuePath}.width`}
                      label="Width (mm)"
                      type="number"
                      component={MuiField}
                      alignRight
                    />
                    <Field
                      xsmd={[6, 6]}
                      name={`${valuePath}.height`}
                      label="Height (mm)"
                      type="number"
                      component={MuiField}
                      alignRight
                    />
                  </>
                )}
              </>
            )}

            <Field
              xsmd={[6, 6]}
              name={`printing.objects.${props.oi}.positioning.centralOffsetX`}
              label="X offset"
              type="number"
              component={MuiField}
              alignRight
            />
            <Field
              xsmd={[6, 6]}
              name={`printing.objects.${props.oi}.positioning.centralOffsetY`}
              label="Y offset"
              type="number"
              component={MuiField}
              alignRight
            />

            {!props.objectVlues.wImg && (
              <>
                <Box>
                  <Field
                    xsmd={[6, 6]}
                    name={`printing.objects.${props.oi}.isHole`}
                    label="Object is a hole"
                    type="checkbox"
                    component={CheckboxField}
                  />
                  <Field
                    xsmd={[6, 6]}
                    name={`printing.objects.${props.oi}.isTransparent`}
                    label="Object is transparent"
                    type="checkbox"
                    component={CheckboxField}
                  />
                </Box>
              </>
            )}

            <Grid container>
              <Grid xs={12} md={6}>
                <StyledMuiFieldLabel label="Color (RAL)" paired={false} />
              </Grid>

              <Grid xs={12} md={6}>
                <FieldArray
                  name={`printing.objects.${props.oi}.colors`}
                  render={(colorArrayHelpers) => (
                    <>
                      {colorArrayHelpers.form.values.printing.objects[
                        props.oi
                      ].colors.map((_: string, ci: number) => (
                        <Box key={ci} display="flex" alignItems="center">
                          <Field
                            name={`printing.objects.${props.oi}.colors.${ci}`}
                            type="rawText"
                            component={MuiField}
                            helperText="color"
                          />
                          <IconButton
                            onClick={() => colorArrayHelpers.remove(ci)}
                          >
                            <DeleteOutline
                              sx={{
                                border: "1px solid #dfe0f2",
                                color: "#250048",
                                padding: "4px",
                                borderRadius: "14px",
                              }}
                            />
                          </IconButton>
                        </Box>
                      ))}
                      <PlusButton
                        label="Add color"
                        onClick={() => colorArrayHelpers.push("")}
                      />
                    </>
                  )}
                />
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </FormGroup>
  )
}

function ObjectFile(props: {
  oi: number
  setBase64: (newFile: string | null) => void
  setFileName: (newImgName: string | null) => void
  fileName: string | null
}): ReactElement {
  return (
    <Grid container alignItems="center">
      <Grid item xs={12} md={6}>
        <StyledMuiFieldLabel label={"Svg file"} paired={false} />
      </Grid>

      <Grid item xs={12} md={6} justifyContent="space-between" direction="row">
        <Button
          sx={{
            alignItems: "start",
            overflow: "hidden",
            width: "83%",
            color: "#3e3259",
            backgroundColor: "white",
            border: "1px solid #dfe0f2",
            borderRadius: "14px",
            maxHeight: "33px",
            height: "33px",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "0px solid black",
            },
            "& .MuiOutlinedInput-root": {
              color: "#250048",
              padding: "0px",
              borderRadius: "14px",
              "&.Mui-focused fieldset": {
                borderColor: "#1a0549",
              },
            },
            "& .MuiOutlinedInput-input": {
              padding: "5px 10px 5px 10px",
            },
            "& .Mui-error": {
              border: "1px solid red",
            },
            input: {
              "&:invalid": {
                borderRadius: "14px",
                border: "1px solid red",
              },
            },
          }}
          onClick={() => {
            document.getElementById(`getFileSVG${props.oi}`)?.click()
          }}
        >
          {props.fileName === null ? "No file selected" : props.fileName}
        </Button>
        <IconButton
          onClick={() => {
            props.setBase64(null)
            props.setFileName(null)
          }}
        >
          <DeleteOutline sx={{ color: "#250048" }} />
        </IconButton>
        <input
          id={`getFileSVG${props.oi}`}
          style={{ display: "none" }}
          type="file"
          accept=".svg"
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            if (event.target.files && event.target.files[0]) {
              const file = event.target.files[0]
              const reader = new FileReader()
              reader.onloadend = () => {
                props.setBase64(reader.result as string)
              }
              reader.readAsDataURL(file)

              props.setFileName(event.target.files[0].name)
            } else {
              props.setBase64(null)
              props.setFileName(null)
            }
          }}
          className="filetype"
        />
      </Grid>
    </Grid>
  )
}
